import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useParams } from 'react-router-dom';
import { Loader } from 'components/layout/misc/Loader';
import Dropdown from '../../components/dropdown/Dropdown';
import EmptyState from '../../components/empty-state/EmptyState';
import { EMPTY_STATE_ICONS } from '../../components/empty-state/emptyStateIcons';
import { jobPositionStore } from '../job-positions/JobPositionStore';
import OpenJobCard from './components/OpenJobCard';
import OpenJobListRow from './components/OpenJobListRow';
import OpenJobProfile from './components/OpenJobProfile';
import OpenJobsHero from './components/OpenJobsHero';
import OpenJobsTopNav from './components/OpenJobsTopNav';
import { getOpenJobCardTheme } from './openJobCardThemes';

const ALL_OPTION = { value: null, label: 'All' };
const SEARCH_DEBOUNCE_MS = 400;
const PAGE_SIZE = 100;

const TYPE_OPTIONS = [ALL_OPTION, ...jobPositionStore.workScheduleOptions];

export default function OpenJobs() {
	const { id } = useParams();
	const selectedId = id ? parseInt(id, 10) : null;
	const isDetailView = Boolean(selectedId);

	const [jobs, setJobs] = useState([]);
	const [totalResults, setTotalResults] = useState(0);
	const [loading, setLoading] = useState(true);
	const [selectedJob, setSelectedJob] = useState(null);
	const [selectedLoading, setSelectedLoading] = useState(isDetailView);
	const [searchInput, setSearchInput] = useState('');
	const [search, setSearch] = useState('');
	const [type, setType] = useState(null);
	const [location, setLocation] = useState(null);
	const [locationOptions, setLocationOptions] = useState([ALL_OPTION]);
	const filterOptionsLoadedRef = useRef(false);
	const hasLoadedRef = useRef(false);

	useEffect(() => {
		document.body.classList.remove('has-navbar-fixed-top');

		return () => {
			if (!document.body.classList.contains('has-navbar-fixed-top')) {
				document.body.classList.add('has-navbar-fixed-top');
			}
		};
	}, []);

	useEffect(() => {
		const timer = setTimeout(() => {
			setSearch(searchInput.trim());
		}, SEARCH_DEBOUNCE_MS);

		return () => clearTimeout(timer);
	}, [searchInput]);

	const fetchJobs = useCallback(async () => {
		setLoading(true);

		try {
			const result = await jobPositionStore.searchPublic({
				title: search || undefined,
				workSchedule: type || undefined,
				location: location || undefined,
				includeFilterOptions: !filterOptionsLoadedRef.current,
				pagination: {
					page: 1,
					limit: PAGE_SIZE
				}
			});

			if (result.status === 'fail') {
				setJobs([]);
				setTotalResults(0);
				return;
			}

			setJobs(result.data?.rows || []);
			setTotalResults(result.data?.pagination?.totalResults || 0);

			if (result.data?.filterOptions?.locations) {
				filterOptionsLoadedRef.current = true;
				setLocationOptions([
					ALL_OPTION,
					...result.data.filterOptions.locations.map(name => ({
						value: name,
						label: name
					}))
				]);
			}
		} finally {
			hasLoadedRef.current = true;
			setLoading(false);
		}
	}, [search, type, location]);

	useEffect(() => {
		void fetchJobs();
	}, [fetchJobs]);

	useEffect(() => {
		if (!selectedId) {
			setSelectedJob(null);
			setSelectedLoading(false);
			return;
		}

		let cancelled = false;
		setSelectedJob(current =>
			current && Number(current.id) === selectedId ? current : null
		);
		setSelectedLoading(true);

		(async () => {
			try {
				const result = await jobPositionStore.getPublic(selectedId);

				if (cancelled) {
					return;
				}

				setSelectedJob(result?.status === 'success' ? result.data : null);
			} finally {
				if (!cancelled) {
					setSelectedLoading(false);
				}
			}
		})();

		return () => {
			cancelled = true;
		};
	}, [selectedId]);

	useEffect(() => {
		if (!isDetailView) {
			document.title = "Prediktive's MIRA";
			return;
		}

		if (selectedLoading) {
			return;
		}

		const positionName = String(selectedJob?.title || '')
			.replace(/\s+/g, ' ')
			.trim();
		document.title = positionName
			? `${positionName} | Prediktive`
			: "Prediktive's MIRA";
	}, [isDetailView, selectedJob, selectedLoading]);

	useEffect(() => {
		return () => {
			document.title = "Prediktive's MIRA";
		};
	}, []);

	const selectedTheme = getOpenJobCardTheme(
		Math.max(
			0,
			jobs.findIndex(job => Number(job.id) === selectedId)
		)
	);

	return (
		<div className="OpenJobs">
			<OpenJobsTopNav />
			<OpenJobsHero />
			<main className="open-jobs-content">
				<h2 className="open-jobs-count">
					Open Jobs ({totalResults.toLocaleString('en-US')})
				</h2>
				<div className="open-jobs-filters">
					<div className="open-jobs-search">
						<input
							type="search"
							placeholder="Search"
							aria-label="Search open jobs"
							value={searchInput}
							onChange={event => setSearchInput(event.target.value)}
						/>
						<img src="/ats/icons/search-black-icon.svg" alt="" />
					</div>
					<Dropdown
						value={type}
						options={TYPE_OPTIONS}
						optionValue="value"
						onChange={setType}
						displayValue={text => `Type: ${text}`}
					/>
					<Dropdown
						value={location}
						options={locationOptions}
						optionValue="value"
						onChange={setLocation}
						displayValue={text => `Location: ${text}`}
					/>
				</div>
				{loading && !hasLoadedRef.current && !isDetailView ? (
					<Loader type="page-loader" className="open-jobs-loader" />
				) : (
					<div className={`open-jobs-results${loading ? ' is-loading' : ''}`}>
						{isDetailView ? (
							<div className="open-jobs-split">
								<div className="open-jobs-list">
									{jobs.length ? (
										jobs.map(job => (
											<OpenJobListRow
												key={job.id}
												job={job}
												selected={Number(job.id) === selectedId}
											/>
										))
									) : loading ? null : (
										<p className="open-jobs-list-empty">
											No matching jobs in this list.
										</p>
									)}
								</div>
								<OpenJobProfile
									job={selectedJob}
									loading={selectedLoading}
									theme={selectedTheme}
								/>
							</div>
						) : jobs.length ? (
							<div className="open-jobs-grid">
								{jobs.map((job, index) => (
									<OpenJobCard key={job.id} job={job} index={index} />
								))}
							</div>
						) : (
							<EmptyState
								icon={EMPTY_STATE_ICONS.job}
								iconAlt="No open jobs"
								message="No open jobs found"
								detail="Try adjusting your search or filters to see more roles."
							/>
						)}
					</div>
				)}
			</main>
		</div>
	);
}
